import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import styled, { css, type DefaultTheme } from 'styled-components';

import { createShouldForwardStyledProp, modeColor, parseDimension, setAlpha } from '../../styles';
import type { ValueOf } from '../../utils/types';
import { FormControlSizes } from '../Form/types';
import { getFontSize, getLineHeight } from '../Form/utils';
import type { IconName } from '../Icons';
import { clippedButtonCss, getButtonBorderRadius } from './utils';

export const ButtonVariants = {
  Priority: 'Priority',
  Primary: 'Primary',
  Positive: 'Positive',
  Negative: 'Negative',
  Warning: 'Warning',
  Default: 'Default',
  /**
   * A button that can be used as an "off"-state for toggle buttons, for example in a button-group
   * with shared background color.
   */
  Muted: 'Muted',
} as const;
export type ButtonVariants = (typeof ButtonVariants)[keyof typeof ButtonVariants];

const ButtonStates = {
  Default: '',
  Hover: 'Hover',
  Focus: 'Focus',
  FocusWithin: 'FocusWithin',
  Active: 'Active',
  Disabled: 'Disabled',
} as const;

export const InternalButtonSpan = styled.span`
  font-size: inherit;
  width: 100%;
`;

export const buttonStyle = ({
  theme,
  size,
  variant,
  disabled,
  ghost,
  prominent,
  dim,
  width,
  height,
  justifyContent = 'center',
  flex,
  pill,
}: {
  theme: DefaultTheme;
  size?: FormControlSizes;
  variant?: ButtonVariants;
  disabled?: boolean;
  ghost?: boolean;
  prominent?: boolean;
  dim?: boolean;
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  justifyContent?: CSSProperties['justifyContent'];
  flex?: CSSProperties['flex'];
  pill?: boolean;
}) => css`
  ${flex &&
  css`
    &&& {
      flex: ${flex};
    }
  `}
  text-decoration: none; // Useful when using forwardedAs={Link}
  position: relative;
  padding: ${getButtonSpacing(theme, size)};
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  border-radius: ${pill ? 10000 : getButtonBorderRadius(theme, size)}px;
  font-size: ${getFontSize(theme, size)}rem;
  ${height && `min-height: ${height}px;`}
  width: ${width ? parseDimension(theme, width) : 'auto'};
  border: solid ${({ theme }) => theme.borderWidthButton}px;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  display: inline-flex;
  gap: ${() => getIconSpacing(size)}px;
  align-items: center;
  justify-content: ${justifyContent};
  text-align: center;
  white-space: nowrap;
  transition:
    box-shadow 200ms ease,
    background 200ms ease,
    border 200ms ease,
    opacity 200ms ease;
  line-height: ${getLineHeight(theme, size)}rem;

  ${buttonColor(theme, variant, disabled, ghost, dim)}
  > ${InternalButtonSpan} {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    flex: 0 1;
  }

  ${prominent && theme.chamferMultiplier > 0 && clippedButtonCss({ size, theme })}
`;

// ButtonWrapper is the styled DOM boundary for buttonStyle. Filter only the
// style-only props consumed by buttonStyle before they reach the button element.
const nonForwardedProps = new Set([
  'dim',
  'flex',
  'ghost',
  'height',
  'justifyContent',
  'pill',
  'prominent',
  'size',
  'variant',
  'width',
]);
export const ButtonWrapper = styled.button
  .attrs<ButtonWrapperProps>(({ type = 'button' }) => ({ type }))
  .withConfig({
    shouldForwardProp: createShouldForwardStyledProp(nonForwardedProps),
  })<ButtonWrapperProps>`
    ${({ theme, size, variant, disabled, ghost, prominent, dim, width, height, justifyContent, flex, pill }) =>
      buttonStyle({
        theme,
        size,
        variant,
        disabled,
        ghost,
        prominent,
        dim,
        width,
        height,
        justifyContent,
        flex,
        pill,
      })}`;

const buttonColor = (
  theme: DefaultTheme,
  variant?: ButtonVariants,
  disabled?: boolean,
  ghost?: boolean,
  dim?: boolean
) => css`
  opacity: ${disabled ? 0.5 : 1};

  ${disabled
    ? getColors(theme, variant, ghost, dim, ButtonStates.Disabled)
    : `
  ${getColors(theme, variant, ghost, dim, ButtonStates.Default)}
  &:focus {
    ${getColors(theme, variant, ghost, dim, ButtonStates.Focus)}
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 1px 1px ${theme.colors.blue.lighten};
    outline: none;
  }
  @media (any-hover: hover) {
    &:hover {
      ${getColors(theme, variant, ghost, dim, ButtonStates.Hover)}
    }
  }
  &:active {
    ${getColors(theme, variant, ghost, dim, ButtonStates.Active)}
  }`}
`;

export type ButtonProps<T = ButtonHTMLAttributes<HTMLButtonElement>> = Omit<T, 'size' | 'height' | 'disabled'> & {
  disabled?: boolean;
  variant?: ButtonVariants;
  size?: FormControlSizes;
  prefix?: ReactNode;
  suffix?: ReactNode;
  startIcon?: IconName;
  endIcon?: IconName;
  /** Ghost styling, which removes the background color and border */
  ghost?: boolean;
  /**
   * If a button is prominent, it will have an altered appearance dependent on the theme.
   * For example, it can have chamfered edges, or be extended to have a different background color.
   */
  prominent?: boolean;
  dim?: boolean;
  done?: boolean;
  loading?: boolean;
  height?: CSSProperties['height'];
  width?: CSSProperties['width'];
  justifyContent?: CSSProperties['justifyContent'];
  flex?: CSSProperties['flex'];
  pill?: boolean;
};

export type ButtonWrapperProps<T = ButtonHTMLAttributes<HTMLButtonElement>> = Omit<
  ButtonProps<T>,
  'done' | 'endIcon' | 'loading' | 'prefix' | 'startIcon' | 'suffix'
>;

const getButtonBaseSpacing = (theme: DefaultTheme, size: FormControlSizes | number = FormControlSizes.Default) => {
  switch (size) {
    case FormControlSizes.Xxs: {
      return theme.spacing2;
    }
    case FormControlSizes.Tiny: {
      return theme.spacing4;
    }
    case FormControlSizes.Small: {
      return theme.spacing4;
    }
    case FormControlSizes.Default: {
      return theme.spacing8;
    }
    case FormControlSizes.Large: {
      return theme.spacing16;
    }
    default: {
      return (theme.spacing8 * size) / FormControlSizes.Default;
    }
  }
};

export const getButtonVerticalSpacing = (
  theme: DefaultTheme,
  size: FormControlSizes | number = FormControlSizes.Default
) => getButtonBaseSpacing(theme, size) - theme.borderWidthButton;

export const getButtonHorizontalSpacing = (theme: DefaultTheme, size: FormControlSizes = FormControlSizes.Default) => {
  const horizontalSpacingTiny = theme.baseSize * 0.375 - theme.borderWidthButton;

  switch (size) {
    case FormControlSizes.Xxs: {
      return horizontalSpacingTiny - 1;
    }
    case FormControlSizes.Tiny: {
      return horizontalSpacingTiny;
    }
    case FormControlSizes.Small: {
      return theme.spacing8 - theme.borderWidthButton;
    }
    case FormControlSizes.Default: {
      return theme.spacing12 - theme.borderWidthButton;
    }
    case FormControlSizes.Large: {
      return theme.spacing16 - theme.borderWidthButton;
    }
  }
};

export const getButtonSpacing = (
  theme: DefaultTheme,
  size: FormControlSizes = FormControlSizes.Default,
  type?: 'switch'
) => {
  // padding cannot be negative -- a negative value here would make the whole shorthand
  // declaration invalid, dropping both axes rather than just clamping the one that went negative.
  const verticalSpacing =
    type === 'switch' ? Math.max(0, getButtonVerticalSpacing(theme, size) - 2) : getButtonVerticalSpacing(theme, size);
  const horizontalSpacing =
    type === 'switch'
      ? Math.max(0, getButtonHorizontalSpacing(theme, size) - 2)
      : getButtonHorizontalSpacing(theme, size);
  return `${verticalSpacing}px ${horizontalSpacing}px`;
};

export const getIconSpacing = (size: FormControlSizes = FormControlSizes.Default) => {
  switch (size) {
    case FormControlSizes.Xxs: {
      return 2;
    }
    case FormControlSizes.Tiny: {
      return 3;
    }
    case FormControlSizes.Small: {
      return 3;
    }
    case FormControlSizes.Default: {
      return 4;
    }
    case FormControlSizes.Large: {
      return 6;
    }
  }
};

export const getIconSize = (size: FormControlSizes = FormControlSizes.Default) => {
  switch (size) {
    case FormControlSizes.Tiny: {
      return 'fontSizeSmall';
    }
    case FormControlSizes.Small: {
      return 'fontSizeDefault';
    }
    case FormControlSizes.Default: {
      return 'fontSizeLarge';
    }
    case FormControlSizes.Large: {
      return 'fontSizeBig';
    }
  }
};

/**
 * Ghost buttons have no fill, so their hover/focus affordance is a subtle overlay. It has to
 * darken on light themes and lighten on dark ones -- a fixed white wash is invisible on light.
 */
const ghostOverlay = (theme: DefaultTheme) =>
  modeColor(theme, setAlpha(0.08, theme.colors.gray['100']), theme.colors.white.mute);

const getColors = (
  theme: DefaultTheme,
  variant: ButtonVariants = ButtonVariants.Default,
  ghost?: boolean,
  dim = false,
  state: ValueOf<typeof ButtonStates> = ButtonStates.Default
): string | undefined => {
  switch (variant) {
    case ButtonVariants.Primary: {
      switch (state) {
        case ButtonStates.Hover: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPrimaryButtonHover : 'none'};
          border-color: ${!ghost ? theme.borderColorPrimaryButtonHover : 'transparent'};
          background: ${!ghost ? theme.backgroundPrimaryButtonHover : theme.colors.primary.mute};
          color: ${!ghost ? theme.colorTextPrimaryButtonHover : theme.colors.primary.lighten};
          `;
        }
        case ButtonStates.Active: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPrimaryButtonActive : 'none'};
          border-color: ${!ghost ? theme.borderColorPrimaryButtonActive : 'transparent'};
          background: ${!ghost ? theme.backgroundPrimaryButtonActive : 'transparent'};
          color: ${!ghost ? theme.colorTextPrimaryButtonActive : theme.colorTextImportant};
          `;
        }
        case ButtonStates.Focus: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPrimaryButtonFocus : 'none'};
          border-color: ${!ghost ? theme.borderColorPrimaryButtonFocus : 'transparent'};
          background: ${!ghost ? theme.backgroundPrimaryButtonFocus : theme.colors.primary.mute};
          color: ${!ghost ? theme.colorTextPrimaryButtonFocus : theme.colorTextImportant};
          `;
        }
        case ButtonStates.Disabled: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPrimaryButtonDisabled : 'none'};
          border-color: ${!ghost ? theme.borderColorPrimaryButtonDisabled : 'transparent'};
          background: ${!ghost ? theme.backgroundPrimaryButtonDisabled : 'transparent'};
          color: ${!ghost ? theme.colorTextPrimaryButtonDisabled : theme.colorTextSubtle};
          `;
        }
        default: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPrimaryButton : 'none'};
          border-color: ${!ghost ? theme.borderColorPrimaryButton : 'transparent'};
          background: ${!ghost ? theme.backgroundPrimaryButton : 'transparent'};
          color: ${!ghost ? theme.colorTextPrimaryButton : theme.colorTextImportant};
          `;
        }
      }
    }
    case ButtonVariants.Positive: {
      switch (state) {
        case ButtonStates.Hover: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPositiveButtonHover : 'none'};
          border-color: ${!ghost ? theme.borderColorPositiveButtonHover : 'transparent'};
          background: ${!ghost ? theme.backgroundPositiveButtonHover : theme.colors.green.mute};
          color: ${!ghost ? theme.colorTextPositiveButtonHover : theme.colors.green.lighten};
          `;
        }
        case ButtonStates.Active: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPositiveButtonActive : 'none'};
          border-color: ${!ghost ? theme.borderColorPositiveButtonActive : 'transparent'};
          background: ${!ghost ? theme.backgroundPositiveButtonActive : 'transparent'};
          color: ${!ghost ? theme.colorTextPositiveButtonActive : theme.colors.green.default};
          `;
        }
        case ButtonStates.Focus: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPositiveButtonFocus : 'none'};
          border-color: ${!ghost ? theme.borderColorPositiveButtonFocus : 'transparent'};
          background: ${!ghost ? theme.backgroundPositiveButtonFocus : theme.colors.green.mute};
          color: ${!ghost ? theme.colorTextPositiveButtonFocus : theme.colors.green.lighten};
          `;
        }
        case ButtonStates.Disabled: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPositiveButtonDisabled : 'none'};
          border-color: ${!ghost ? theme.borderColorPositiveButtonDisabled : 'transparent'};
          background: ${!ghost ? theme.backgroundPositiveButtonDisabled : 'transparent'};
          color: ${!ghost ? theme.colorTextPositiveButtonDisabled : theme.colors.green.default};
          `;
        }
        default: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPositiveButton : 'none'};
          border-color: ${!ghost ? theme.borderColorPositiveButton : 'transparent'};
          background: ${!ghost ? theme.backgroundPositiveButton : 'transparent'};
          color: ${!ghost ? theme.colorTextPositiveButton : theme.colors.green.lighten};
          `;
        }
      }
    }
    case ButtonVariants.Negative: {
      switch (state) {
        case ButtonStates.Hover: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowNegativeButtonHover : 'none'};
          border-color: ${!ghost ? theme.borderColorNegativeButtonHover : 'transparent'};
          background: ${!ghost ? theme.backgroundNegativeButtonHover : theme.colors.red.mute};
          color: ${!ghost ? theme.colorTextNegativeButtonHover : theme.colors.red.lighten};
          `;
        }
        case ButtonStates.Active: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowNegativeButtonActive : 'none'};
          border-color: ${!ghost ? theme.borderColorNegativeButtonActive : 'transparent'};
          background: ${!ghost ? theme.backgroundNegativeButtonActive : 'transparent'};
          color: ${!ghost ? theme.colorTextNegativeButtonActive : theme.colors.red.default};
          `;
        }
        case ButtonStates.Focus: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowNegativeButtonFocus : 'none'};
          border-color: ${!ghost ? theme.borderColorNegativeButtonFocus : 'transparent'};
          background: ${!ghost ? theme.backgroundNegativeButtonFocus : theme.colors.red.mute};
          color: ${!ghost ? theme.colorTextNegativeButtonFocus : theme.colors.red.lighten};
          `;
        }
        case ButtonStates.Disabled: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowNegativeButtonDisabled : 'none'};
          border-color: ${!ghost ? theme.borderColorNegativeButtonDisabled : 'transparent'};
          background: ${!ghost ? theme.backgroundNegativeButtonDisabled : 'transparent'};
          color: ${!ghost ? theme.colorTextNegativeButtonDisabled : theme.colors.red.lighten};
          `;
        }
        default: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowNegativeButton : 'none'};
          border-color: ${!ghost ? theme.borderColorNegativeButton : 'transparent'};
          background: ${!ghost ? theme.backgroundNegativeButton : 'transparent'};
          color: ${!ghost ? theme.colorTextNegativeButton : theme.colors.red.lighten};
          `;
        }
      }
    }
    case ButtonVariants.Priority: {
      switch (state) {
        case ButtonStates.Hover: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPriorityButtonHover : 'none'};
          border-color: ${!ghost ? theme.borderColorPriorityButtonHover : 'transparent'};
          background: ${!ghost ? theme.backgroundPriorityButtonHover : ghostOverlay(theme)};
          color: ${!ghost ? theme.colorTextPriorityButtonHover : theme.colors.gray['100']};
          `;
        }
        case ButtonStates.Active: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPriorityButtonActive : 'none'};
          border-color: ${!ghost ? theme.borderColorPriorityButtonActive : 'transparent'};
          background: ${!ghost ? theme.backgroundPriorityButtonActive : 'transparent'};
          color: ${!ghost ? theme.colorTextPriorityButtonActive : theme.colors.gray['100']};
          `;
        }
        case ButtonStates.Focus: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPriorityButtonFocus : 'none'};
          border-color: ${!ghost ? theme.borderColorPriorityButtonFocus : 'transparent'};
          background: ${!ghost ? theme.backgroundPriorityButtonFocus : ghostOverlay(theme)};
          color: ${!ghost ? theme.colorTextPriorityButtonFocus : theme.colors.gray['100']};
          `;
        }
        case ButtonStates.Disabled: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPriorityButtonDisabled : 'none'};
          border-color: ${!ghost ? theme.borderColorPriorityButtonDisabled : 'transparent'};
          background: ${!ghost ? theme.backgroundPriorityButtonDisabled : 'transparent'};
          color: ${!ghost ? theme.colorTextPriorityButtonDisabled : theme.colors.gray['100']};
          `;
        }
        default: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowPriorityButton : 'none'};
          border-color: ${!ghost ? theme.borderColorPriorityButton : 'transparent'};
          background: ${!ghost ? theme.backgroundPriorityButton : 'transparent'};
          color: ${
            !ghost ? (!dim ? theme.colorTextPriorityButton : theme.colors.gray['100']) : theme.colors.gray['100']
          };
          `;
        }
      }
    }
    case ButtonVariants.Muted: {
      switch (state) {
        case ButtonStates.Hover: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButtonHover : 'none'};
          border-color: ${!ghost ? theme.borderColorMutedButtonHover : 'transparent'};
          background: ${!ghost ? theme.backgroundMutedButtonHover : ghostOverlay(theme)};
          color: ${theme.colorTextMutedButtonHover};
          `;
        }
        case ButtonStates.Active: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButtonActive : 'none'};
          border-color: ${!ghost ? theme.borderColorMutedButtonActive : 'transparent'};
          background: ${!ghost ? theme.backgroundMutedButtonActive : 'transparent'};
          color: ${theme.colorTextMutedButtonActive};
          `;
        }
        case ButtonStates.Focus: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButtonFocus : 'none'};
          border-color: ${!ghost ? theme.borderColorMutedButtonFocus : 'transparent'};
          background: ${!ghost ? theme.backgroundMutedButtonFocus : ghostOverlay(theme)};
          color: ${!ghost ? theme.colorTextMutedButtonFocus : theme.colorTextImportant};
          `;
        }
        case ButtonStates.Disabled: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButtonDisabled : 'none'};
          border-color: ${!ghost ? theme.borderColorMutedButtonDisabled : 'transparent'};
          background: ${!ghost ? theme.backgroundMutedButtonDisabled : 'transparent'};
          color: ${!ghost ? theme.colorTextMutedButtonDisabled : theme.colorTextImportant};
          `;
        }
        default: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButton : 'none'};
          border-color: ${!ghost ? theme.borderColorMutedButton : 'transparent'};
          background: ${!ghost ? theme.backgroundMutedButton : 'transparent'};
          color: ${!ghost ? (!dim ? theme.colorTextMutedButton : theme.colors.gray['090']) : theme.colorTextImportant};
          `;
        }
      }
    }
    case ButtonVariants.Warning: {
      switch (state) {
        case ButtonStates.Hover: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButtonHover : 'none'};
          border-color: ${!ghost ? theme.borderColorWarningButtonHover : 'transparent'};
          background: ${!ghost ? theme.backgroundWarningButtonHover : theme.colors.yellow.mute};
          color: ${!ghost ? theme.colorTextWarningButtonHover : theme.colors.yellow.lighten};
          `;
        }
        case ButtonStates.Active: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButtonActive : 'none'};
          border-color: ${!ghost ? theme.borderColorWarningButtonActive : 'transparent'};
          background: ${!ghost ? theme.backgroundWarningButtonActive : 'transparent'};
          color: ${!ghost ? theme.colorTextWarningButtonActive : theme.colors.yellow.default};
          `;
        }
        case ButtonStates.Focus: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowDefaultButtonFocus : 'none'};
          border-color: ${!ghost ? theme.borderColorWarningButtonFocus : 'transparent'};
          background: ${!ghost ? theme.backgroundWarningButtonFocus : theme.colors.yellow.mute};
          color: ${!ghost ? theme.colorTextWarningButtonFocus : theme.colors.yellow.lighten};
          `;
        }
        case ButtonStates.Disabled: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButtonDisabled : 'none'};
          border-color: ${!ghost ? theme.borderColorWarningButtonDisabled : 'transparent'};
          background: ${!ghost ? theme.backgroundWarningButtonDisabled : 'transparent'};
          color: ${!ghost ? theme.colorTextWarningButtonDisabled : theme.colors.yellow.default};
          `;
        }
        default: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowMutedButton : 'none'};
          border-color: ${!ghost ? theme.borderColorWarningButton : 'transparent'};
          background: ${!ghost ? theme.backgroundWarningButton : 'transparent'};
          color: ${!ghost ? theme.colorTextWarningButton : theme.colors.yellow.lighten};
          `;
        }
      }
    }
    case ButtonVariants.Default:
    default: {
      switch (state) {
        case ButtonStates.Hover: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowDefaultButtonHover : 'none'};
          border-color: ${!ghost ? theme.borderColorDefaultButtonHover : 'transparent'};
          background: ${!ghost ? theme.backgroundDefaultButtonHover : ghostOverlay(theme)};
          color: ${theme.colorTextDefaultButtonHover};
          `;
        }
        case ButtonStates.Active: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowDefaultButtonActive : 'none'};
          border-color: ${!ghost ? theme.borderColorDefaultButtonActive : 'transparent'};
          background: ${!ghost ? theme.backgroundDefaultButtonActive : 'transparent'};
          color: ${theme.colorTextDefaultButtonActive};
          `;
        }
        case ButtonStates.Focus: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowDefaultButtonFocus : 'none'};
          border-color: ${!ghost ? theme.borderColorDefaultButtonFocus : 'transparent'};
          background: ${!ghost ? theme.backgroundDefaultButtonFocus : ghostOverlay(theme)};
          color: ${theme.colorTextDefaultButtonFocus};
          `;
        }
        case ButtonStates.Disabled: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowDefaultButtonDisabled : 'none'};
          border-color: ${!ghost ? theme.borderColorDefaultButtonDisabled : 'transparent'};
          background: ${!ghost ? theme.backgroundDefaultButtonDisabled : 'transparent'};
          color: ${theme.colorTextDefaultButtonDisabled};
          `;
        }
        default: {
          return `
          box-shadow: ${!ghost ? theme.boxShadowDefaultButton : 'none'};
          border-color: ${!ghost ? theme.borderColorDefaultButton : 'transparent'};
          background: ${!ghost ? theme.backgroundDefaultButton : 'transparent'};
          color: ${!dim ? theme.colorTextDefaultButton : theme.colors.gray['090']};
          `;
        }
      }
    }
  }
};

export function getIconButtonSize(theme: DefaultTheme, size: FormControlSizes | number = FormControlSizes.Default) {
  // This is set as an explicit width/height, and the global box-sizing: border-box reset means
  // padding and border are absorbed inside that size rather than added to it, so this does not
  // need to account for borderWidthButton.
  return getButtonBaseSpacing(theme, size) * 2 + getLineHeight(theme, size) * theme.baseSize;
}
